import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { openTimeTrack, useTimeTrackAccess, useTimeTrackTargets, type TargetType } from "@/hooks/use-timetrack";

interface Summary {
  summaries: { targetType: TargetType; targetId: string; directSeconds: number; totalSeconds: number }[];
}
/** All rows in a project share one cache entry; never query once per pulse. */
export function TimeTrackSummary({ projectId, targetType, targetId }: {
  projectId: string; targetType: TargetType; targetId: string | number;
}) {
  const { user, enabled } = useTimeTrackAccess();
  const targets = useTimeTrackTargets();
  const authorized = targets.data?.targets.some(t => t.targetType === targetType && t.targetId === String(targetId));
  const { data, isError } = useQuery<Summary>({
    queryKey: ["timetrack", user?.id, "summary", projectId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/timetrack/summaries?projectId=${encodeURIComponent(projectId)}`);
      return response.json();
    },
    enabled: enabled && !!authorized, staleTime: 30_000, retry: false,
  });
  if (!enabled || isError) return null;
  const summary = data?.summaries.find(s => s.targetType === targetType && s.targetId === String(targetId));
  if (!summary || !summary.totalSeconds) return null;
  const format = (seconds: number) => {
    const minutes = Math.round(seconds / 60);
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };
  return <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground whitespace-nowrap"
    title={`Direct: ${format(summary.directSeconds)} · Total including children: ${format(summary.totalSeconds)}`}
    onClick={event => {
      event.stopPropagation();
      openTimeTrack({ view: "project", projectId, targetType, targetId });
    }}>
    {format(summary.totalSeconds)}
  </button>;
}
